import React, { memo, forwardRef, useImperativeHandle, useMemo, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import Modal from "@mui/material/Modal";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";

import api from "services/api";
import ERRORS from "common/errors";
import { getISOString, isMaxDateBeforeToday } from "common/calendar";
import { promptAlertMessage } from "store/slices/alert";
import serveRequestErrors from "common/serve-request-errors";
import AppButton from "components/app-button";
import AppCalendarInput from "components/app-calendar-input";

export const AppOvertimeEditExpiryModal = (props, ref) => {
	const dispatch = useDispatch();
	const [overtimeId, setOvertimeId] = useState(null);
	const [visible, setVisible] = useState(false);
	const today = useMemo(() => getISOString(new Date().setHours(0, 0, 0, 0)), []);
	const initialValues = useMemo(() => ({ expiryDate: "" }), []);

	//prettier-ignore
	const formik = useFormik({
		initialValues,
		validationSchema: yup.object({
			expiryDate: yup.string().required(ERRORS.REQUIRED).test("expiryDate", ERRORS.EXPIRY_DATE_INVALID, (value) => {
				if (!value) return true;

				return !isMaxDateBeforeToday(value, today);
			}),
		}),
		onSubmit: (values) => {
			onHandleConfirm(values);
		},
	});

	const onHandleDismiss = useCallback(() => {
		setVisible(false);
	}, []);

	//prettier-ignore
	const onHandleShow = useCallback((record) => {
		setOvertimeId(record.id);
		formik.resetForm({ values: { expiryDate: record.expiryLeaveDate || "" } });
		setVisible(true);
	}, [formik]);

	//prettier-ignore
	const onHandleConfirm = useCallback(async (values) => {
		let response = null;

		try {
			await api.post.overtime.edit({ id: overtimeId, expiryDate: values.expiryDate });
			response = true;
		} catch (error) {
			serveRequestErrors(error);
		} finally {
			formik.setSubmitting(false);
		}

		if (response) {
			onHandleDismiss();
			dispatch(promptAlertMessage({ message: "Expiry date has been updated successfully" }));
			props.onHandleGetList();
		}
	}, [dispatch, overtimeId, formik, onHandleDismiss, props]);

	//prettier-ignore
	useImperativeHandle(ref, () => ({
		onHandleShow: onHandleShow,
		onHandleDismiss: onHandleDismiss,
	}));

	return (
		<Modal classes={{ root: "app-overtime-edit-expiry-modal" }} open={visible} onClose={onHandleDismiss} aria-labelledby="overtime-edit-expiry-modal" aria-describedby="overtime-edit-expiry-modal">
			<div className="overtime-edit-expiry-modal">
				<div className="overtime-edit-expiry-modal__main">
					<div className="overtime-edit-expiry-modal__header">
						<p className="overtime-edit-expiry-modal__title">Edit Expiry Date</p>
						<p className="overtime-edit-expiry-modal__description">Select a new expiry date for this replacement leave.</p>
					</div>

					<form className="overtime-edit-expiry-modal__body" onSubmit={formik.handleSubmit}>
						{/* prettier-ignore */}
						<AppCalendarInput required name="expiryDate" label="Expiry Date" placeholder="Select date" minDate={today} value={formik.values.expiryDate} error={formik.errors.expiryDate} touched={formik.touched.expiryDate} disabled={formik.isSubmitting} onChange={formik.setFieldValue} />

						<div className="overtime-edit-expiry-modal__button-container">
							<AppButton type="button" label="Cancel" outline onClick={onHandleDismiss} />
							<AppButton type="submit" label="Confirm" disabled={formik.isSubmitting} />
						</div>
					</form>
				</div>
			</div>
		</Modal>
	);
};

export default memo(forwardRef(AppOvertimeEditExpiryModal));

AppOvertimeEditExpiryModal.propTypes = {
	ref: PropTypes.object,
	onHandleGetList: PropTypes.func,
};
